import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  TrendingUp,
  Bell,
  CreditCard,
  Settings,
  User,
  LogOut,
  Menu,
  X,
  Shield,
  ChevronRight,
  Home,
  Info,
  Mail,
  Crown,
  ShoppingCart
} from "lucide-react";

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItems = [
    { href: "/subscription", label: "Manage Subscription / Upgrade Access", icon: Crown },
    { href: "/notifications", label: "Notifications", icon: Bell },
    { href: "/payment-methods", label: "Payment Methods", icon: CreditCard },
    { href: "/subscription", label: "Subscription", icon: Settings },
    { href: "/profile", label: "Profile", icon: User },
  ];

  const goldArbitrageItems = [
    { href: "/gold-purchase-recommendation", label: "GEOCORE Gold Purchase Speculation", icon: ShoppingCart },
    { href: "/gold-sales-recommendation", label: "Gold Sales Recommendation", icon: TrendingUp },
  ];

  const infoItems = [
    { href: "/", label: "Home", icon: Home },
    { href: "/about", label: "About Us", icon: Info },
  ];

  const supportEmail = "sovereignsurety@gmail.com";

  const adminItems = [
    { href: "/admin", label: "Admin Panel", icon: Shield },
  ];

  const getTierBadgeClass = (tier) => {
    switch (tier) {
      case "vip":
        return "tier-badge-vip";
      case "subscriber":
        return "tier-badge-subscriber";
      default:
        return "tier-badge-follower";
    }
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_project-kickoff-24/artifacts/si6ptofl_Burundi%20Flag%20EMBOSSED.jpg" 
            alt="GeoCore Global" 
            className="h-12 w-auto"
          />
        </Link>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{user?.name}</p>
            <Badge className={`text-xs ${getTierBadgeClass(user?.subscription_tier)}`}>
              {user?.subscription_tier?.toUpperCase()}
            </Badge>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {/* Govt. Gold Arbitrage Section — pinned to top */}
          <div className="pb-2">
            <p className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Govt. Gold Arbitrage
            </p>
          </div>
          {goldArbitrageItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}

          {/* Divider before main nav */}
          <div className="pt-4" />

          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.label}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}

          {user?.role === "admin" && (
            <>
              <div className="pt-4 pb-2">
                <p className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Admin
                </p>
              </div>
              {adminItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    }`}
                    data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </>
          )}

          {/* Information Section */}
          <div className="pt-4 pb-2">
            <p className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Information
            </p>
          </div>
          {infoItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            );
          })}
          
          {/* Support Email Link */}
          <a
            href={`mailto:${supportEmail}?subject=Support Request - GeoCore Global`}
            className="flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary"
            data-testid="sidebar-support"
          >
            <Mail className="w-5 h-5" />
            <span className="text-sm font-medium">Support</span>
          </a>
        </nav>
      </ScrollArea>

      {/* Logout */}
      <div className="p-4 border-t border-border">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
          onClick={handleLogout}
          data-testid="sidebar-logout"
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-10 left-0 right-0 z-50 bg-background border-b border-border">
        <div className="flex items-center justify-between px-4 h-20">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_project-kickoff-24/artifacts/si6ptofl_Burundi%20Flag%20EMBOSSED.jpg" 
              alt="GeoCore Global" 
              className="h-10 w-auto"
            />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-10 left-0 z-50 h-[calc(100vh-2.5rem)] w-64 bg-card border-r border-border transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-[7.5rem] lg:pt-10 min-h-screen">
        <div className="p-6 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
